import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from 'react-hot-toast';
import ErrorBoundary from './components/ErrorBoundary';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import LoadingScreen from './components/LoadingScreen';
import FeedbackButton from './components/FeedbackButton';
import AdWrapper from './components/AdWrapper';
import NotFound from './pages/NotFound';
import { useScrollToTop } from './utils/scrollToTop';

// Lazy load pages
const Home = lazy(() => import('./pages/Home'));
const DateDifference = lazy(() => import('./pages/DateDifference'));
const AgeCalculator = lazy(() => import('./pages/AgeCalculator'));
const CountdownTimer = lazy(() => import('./pages/CountdownTimer'));
const Stopwatch = lazy(() => import('./pages/Stopwatch'));
const WorkdayCalculator = lazy(() => import('./pages/WorkdayCalculator'));
const TimeZoneConverter = lazy(() => import('./pages/TimeZoneConverter'));
const UnitConverter = lazy(() => import('./pages/UnitConverter'));
const RandomGenerator = lazy(() => import('./pages/RandomGenerator'));
const PercentageCalculator = lazy(() => import('./pages/PercentageCalculator'));
const DailyAffirmation = lazy(() => import('./pages/DailyAffirmation'));
const TaskBoard = lazy(() => import('./pages/TaskBoard'));
const PasswordGenerator = lazy(() => import('./pages/PasswordGenerator'));
const TextCounter = lazy(() => import('./pages/TextCounter'));
const TextCompare = lazy(() => import('./pages/TextCompare'));
const Calculator = lazy(() => import('./pages/Calculator'));
const ColorConverter = lazy(() => import('./pages/ColorConverter'));
const QRCodeGenerator = lazy(() => import('./pages/QRCodeGenerator'));
const LoremIpsumGenerator = lazy(() => import('./pages/LoremIpsumGenerator'));
const PDFConverter = lazy(() => import('./pages/PDFConverter'));
const Notes = lazy(() => import('./pages/Notes'));
const BackgroundRemover = lazy(() => import('./pages/BackgroundRemover'));
const ZodiacFinder = lazy(() => import('./pages/ZodiacFinder'));
const Weather = lazy(() => import('./pages/Weather'));
const RandomFact = lazy(() => import('./pages/RandomFact'));
const BuzzwordGenerator = lazy(() => import('./pages/BuzzwordGenerator'));
const ChuckNorris = lazy(() => import('./pages/ChuckNorris'));
const MemeMaker = lazy(() => import('./pages/MemeMaker'));
const JsonValidator = lazy(() => import('./pages/JsonValidator'));
const DecisionMakerWheel = lazy(() => import('./pages/DecisionMakerWheel'));
const NumberBaseConverter = lazy(() => import('./pages/NumberBaseConverter'));
const BmiCalculator = lazy(() => import('./pages/BmiCalculator'));
const LoanCalculator = lazy(() => import('./pages/LoanCalculator'));
const OnlineWhiteboard = lazy(() => import('./pages/OnlineWhiteboard'));
const Base64Converter = lazy(() => import('./pages/Base64Converter'));
const CaseConverter = lazy(() => import('./pages/CaseConverter'));
const CreditCardValidator = lazy(() => import('./pages/CreditCardValidator'));
const FuelCostCalculator = lazy(() => import('./pages/FuelCostCalculator'));
const HtmlEntityConverter = lazy(() => import('./pages/HtmlEntityConverter'));
const PlaceholderImageGenerator = lazy(() => import('./pages/PlaceholderImageGenerator')); // Original import
const MorseCodeTranslator = lazy(() => import('./pages/MorseCodeTranslator'));
const PomodoroTimer = lazy(() => import('./pages/PomodoroTimer'));
const RandomNameGenerator = lazy(() => import('./pages/RandomNameGenerator'));
const RemoveDuplicateLines = lazy(() => import('./pages/RemoveDuplicateLines'));
const TextReverser = lazy(() => import('./pages/TextReverser'));
const TextSortingTool = lazy(() => import('./pages/TextSortingTool'));
const TipCalculator = lazy(() => import('./pages/TipCalculator'));
const UnixTimestampConverter = lazy(() => import('./pages/UnixTimestampConverter'));
const UrlEncoderDecoder = lazy(() => import('./pages/UrlEncoderDecoder')); // Added Tool #26
const BrailleTranslator = lazy(() => import('./pages/BrailleTranslator')); // Added Tool #27
const ColorPicker = lazy(() => import('./pages/ColorPicker')); // Added Tool #28
const CssGradientGenerator = lazy(() => import('./pages/CssGradientGenerator')); // Added Tool #29
const CsvJsonConverter = lazy(() => import('./pages/CsvJsonConverter')); // Added Tools #30 & #37
const DataUriGenerator = lazy(() => import('./pages/DataUriGenerator')); // Added Tool #31
const HashingTool = lazy(() => import('./pages/HashingTool')); // Added Tool #32
const HtaccessRedirectGenerator = lazy(() => import('./pages/HtaccessRedirectGenerator')); // Added Tool #33
const HtmlTableGenerator = lazy(() => import('./pages/HtmlTableGenerator')); // Added Tool #34
const IbanValidator = lazy(() => import('./pages/IbanValidator')); // Added Tool #35
const InflationCalculator = lazy(() => import('./pages/InflationCalculator')); // Added Tool #36
const MarkdownEditor = lazy(() => import('./pages/MarkdownEditor')); // Added Tool #61
const PasswordStrengthChecker = lazy(() => import('./pages/PasswordStrengthChecker')); // Added Tool #62
// Removed duplicate PlaceholderImageGenerator import from previous line 77
const RegexTester = lazy(() => import('./pages/RegexTester')); // Added Tool #64
const RobotsTxtGenerator = lazy(() => import('./pages/RobotsTxtGenerator')); // Added Tool #65
const SalesTaxCalculator = lazy(() => import('./pages/SalesTaxCalculator')); // Added Tool #66
const SubnetCalculator = lazy(() => import('./pages/SubnetCalculator')); // Added Tool #67
const UnicodeCharacterInfo = lazy(() => import('./pages/UnicodeCharacterInfo')); // Added Tool #68
const XmlValidator = lazy(() => import('./pages/XmlValidator')); // Added Tool #69
const AsciiArtGenerator = lazy(() => import('./pages/AsciiArtGenerator')); // Added Tool #70
const CitationGenerator = lazy(() => import('./pages/CitationGenerator').then(module => ({ default: module.default }))); // Added Tool #71
const CodeFormatter = lazy(() => import('./pages/CodeFormatter')); // Added Tool #72
const CodeMinifier = lazy(() => import('./pages/CodeMinifier')); // Added Tool #73
const ColorPaletteGenerator = lazy(() => import('./pages/ColorPaletteGenerator')); // Added Tool #74
const CssValidator = lazy(() => import('./pages/CssValidator')); // Added Tool #75
const BlogIndex = lazy(() => import('./pages/Blog/BlogIndex')); // Added Blog Index
const BlogPost = lazy(() => import('./pages/Blog/BlogPost')); // Added Blog Post Page
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Contact = lazy(() => import('./pages/Contact'));

function AppContent() {
  useScrollToTop();

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-black text-gray-100 transition-colors duration-200">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <main
          id="main-content"
          className="flex-grow container mx-auto px-4 py-8"
          tabIndex={-1}
          style={{ outline: 'none' }}
        >
          <Suspense fallback={<LoadingScreen />}>
            <AdWrapper>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/blog" element={<BlogIndex />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/task-board" element={<TaskBoard />} />
                <Route path="/notes" element={<Notes />} />
                <Route path="/calculator" element={<Calculator />} />
                <Route path="/color-converter" element={<ColorConverter />} />
                <Route path="/qr-code-generator" element={<QRCodeGenerator />} />
                <Route path="/lorem-ipsum" element={<LoremIpsumGenerator />} />
                <Route path="/pdf-converter" element={<PDFConverter />} />
                <Route path="/background-remover" element={<BackgroundRemover />} />
                <Route path="/date-difference" element={<DateDifference />} />
                <Route path="/age-calculator" element={<AgeCalculator />} />
                <Route path="/countdown-timer" element={<CountdownTimer />} />
                <Route path="/stopwatch" element={<Stopwatch />} />
                <Route path="/workday-calculator" element={<WorkdayCalculator />} />
                <Route path="/timezone-converter" element={<TimeZoneConverter />} />
                <Route path="/unit-converter" element={<UnitConverter />} />
                <Route path="/random-generator" element={<RandomGenerator />} />
                <Route path="/percentage-calculator" element={<PercentageCalculator />} />
                <Route path="/password-generator" element={<PasswordGenerator />} />
                <Route path="/daily-affirmation" element={<DailyAffirmation />} />
                <Route path="/text-counter" element={<TextCounter />} />
                <Route path="/text-compare" element={<TextCompare />} />
                <Route path="/zodiac-finder" element={<ZodiacFinder />} />
                <Route path="/weather" element={<Weather />} />
                <Route path="/random-fact" element={<RandomFact />} />
                <Route path="/buzzword-generator" element={<BuzzwordGenerator />} />
                <Route path="/chuck-norris" element={<ChuckNorris />} />
                <Route path="/meme-maker" element={<MemeMaker />} />
                <Route path="/json-validator" element={<JsonValidator />} />
                <Route path="/decision-maker-wheel" element={<DecisionMakerWheel />} />
                <Route path="/number-base-converter" element={<NumberBaseConverter />} />
                <Route path="/bmi-calculator" element={<BmiCalculator />} />
                <Route path="/loan-calculator" element={<LoanCalculator />} />
                <Route path="/online-whiteboard" element={<OnlineWhiteboard />} />
                {/* Newly added tools */}
                <Route path="/base64-converter" element={<Base64Converter />} />
                <Route path="/case-converter" element={<CaseConverter />} />
                <Route path="/credit-card-validator" element={<CreditCardValidator />} />
                <Route path="/fuel-cost-calculator" element={<FuelCostCalculator />} />
                <Route path="/html-entity-converter" element={<HtmlEntityConverter />} />
                <Route path="/placeholder-image-generator" element={<PlaceholderImageGenerator />} />
                <Route path="/morse-code-translator" element={<MorseCodeTranslator />} />
                <Route path="/pomodoro-timer" element={<PomodoroTimer />} />
                <Route path="/random-name-generator" element={<RandomNameGenerator />} />
                <Route path="/remove-duplicate-lines" element={<RemoveDuplicateLines />} />
                <Route path="/text-reverser" element={<TextReverser />} />
                <Route path="/text-sorting-tool" element={<TextSortingTool />} />
                <Route path="/tip-calculator" element={<TipCalculator />} />
                <Route path="/unix-timestamp-converter" element={<UnixTimestampConverter />} />
                <Route path="/url-encoder-decoder" element={<UrlEncoderDecoder />} /> {/* Added Tool #26 */}
                <Route path="/braille-translator" element={<BrailleTranslator />} /> {/* Added Tool #27 */}
                <Route path="/color-picker" element={<ColorPicker />} /> {/* Added Tool #28 */}
                <Route path="/css-gradient-generator" element={<CssGradientGenerator />} /> {/* Added Tool #29 */}
                <Route path="/csv-json-converter" element={<CsvJsonConverter />} /> {/* Added Tools #30 & #37 */}
                <Route path="/data-uri-generator" element={<DataUriGenerator />} /> {/* Added Tool #31 */}
                <Route path="/hashing-tool" element={<HashingTool />} /> {/* Added Tool #32 */}
                <Route path="/htaccess-redirect-generator" element={<HtaccessRedirectGenerator />} /> {/* Added Tool #33 */}
                <Route path="/html-table-generator" element={<HtmlTableGenerator />} /> {/* Added Tool #34 */}
                <Route path="/iban-validator" element={<IbanValidator />} /> {/* Added Tool #35 */}
                <Route path="/inflation-calculator" element={<InflationCalculator />} /> {/* Added Tool #36 */}
                <Route path="/markdown-editor" element={<MarkdownEditor />} /> {/* Added Tool #61 */}
                <Route path="/password-strength-checker" element={<PasswordStrengthChecker />} /> {/* Added Tool #62 */}
                <Route path="/placeholder-image-generator" element={<PlaceholderImageGenerator />} /> {/* Added Tool #63 */}
                <Route path="/regex-tester" element={<RegexTester />} /> {/* Added Tool #64 */}
                <Route path="/robots-txt-generator" element={<RobotsTxtGenerator />} /> {/* Added Tool #65 */}
                <Route path="/sales-tax-calculator" element={<SalesTaxCalculator />} /> {/* Added Tool #66 */}
                <Route path="/subnet-calculator" element={<SubnetCalculator />} /> {/* Added Tool #67 */}
                <Route path="/unicode-character-info" element={<UnicodeCharacterInfo />} /> {/* Added Tool #68 */}
                <Route path="/xml-validator" element={<XmlValidator />} /> {/* Added Tool #69 */}
                <Route path="/ascii-art-generator" element={<AsciiArtGenerator />} /> {/* Added Tool #70 */}
                <Route path="/citation-generator" element={<CitationGenerator />} /> {/* Added Tool #71 */}
                <Route path="/code-formatter" element={<CodeFormatter />} /> {/* Added Tool #72 */}
                <Route path="/code-minifier" element={<CodeMinifier />} /> {/* Added Tool #73 */}
                <Route path="/color-palette-generator" element={<ColorPaletteGenerator />} /> {/* Added Tool #74 */}
                <Route path="/css-validator" element={<CssValidator />} /> {/* Added Tool #75 */}
                {/* End newly added tools */}
                {/* Legal and Info Pages */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AdWrapper>
          </Suspense>
        </main>
        <Footer />
      </div>
      <CookieConsent />
      <FeedbackButton />
      <Toaster
        position="bottom-right"
        toastOptions={{
          className: 'bg-gray-800 text-gray-100',
          style: {
            background: 'rgb(31, 41, 55)',
            color: 'rgb(243, 244, 246)',
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <ThemeProvider>
          <AppContent />
          </ThemeProvider>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;